import { localDayISO } from './localDay.js'

// "On this day" (IDEA-MEMORY-ANNIVERSARY-001): memories saved on today's month
// and day in an earlier year resurface on Home. Two owner-only sources feed it:
// share_log (Gave / Received / Connection) and memory_anchors (Home "Add a memory").
//
// The database is asked for exact dates, not "every old memory": one date per
// earlier year, so the read stays a handful of rows however long a collector has
// used the app. A Feb 29 memory resurfaces on Feb 28 in years without a Feb 29.

export const ANNIVERSARY_LOOKBACK_YEARS = 30

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function parseDay(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!match) return null
  return { year: Number(match[1]), month: match[2], day: match[3] }
}

/** The earlier-year dates whose memories belong to `today` (YYYY-MM-DD), newest first. */
export function anniversaryDates(today = localDayISO(), lookbackYears = ANNIVERSARY_LOOKBACK_YEARS) {
  const parsed = parseDay(today)
  if (!parsed) return []
  const { year, month, day } = parsed
  const dates = []
  for (let back = 1; back <= lookbackYears; back += 1) {
    const past = year - back
    if (month === '02' && day === '29') continue
    dates.push(`${past}-${month}-${day}`)
    if (month === '02' && day === '28' && !isLeapYear(year) && isLeapYear(past)) {
      dates.push(`${past}-02-29`)
    }
  }
  if (month === '02' && day === '29') {
    for (let back = 1; back <= lookbackYears; back += 1) {
      const past = year - back
      if (isLeapYear(past)) dates.push(`${past}-02-29`)
    }
  }
  return dates
}

const SHARE_KINDS = new Set(['given', 'received', 'connection'])

// Home's collection is grouped: one representative row per scent, with the other
// collection rows (sizes, concentrations) under sizes[]. A memory can hang off any
// of them, so index every id and keep the exact one, as sotdHero does for wears.
function ownedBottlesById(fragrances) {
  const byId = new Map()
  for (const group of fragrances) {
    if (!group?.id) continue
    byId.set(group.id, group)
    for (const size of group.sizes || []) {
      if (size?.id && !byId.has(size.id)) byId.set(size.id, { ...group, collection_group_id: group.id, id: size.id })
    }
  }
  return byId
}

/**
 * Normalize share_log and memory_anchors rows into one list of anniversaries for
 * `today`, keeping only bottles still in the collection. Oldest memory first:
 * "three years ago" is the stronger pull than "one year ago".
 */
export function anniversariesForDay({ shares = [], anchors = [], fragrances = [], today = localDayISO() }) {
  const wanted = new Set(anniversaryDates(today))
  const currentYear = parseDay(today)?.year
  if (!currentYear || wanted.size === 0) return []
  const byId = ownedBottlesById(fragrances)
  const items = []

  for (const row of shares) {
    if (!SHARE_KINDS.has(row?.direction) || !wanted.has(row.shared_on)) continue
    const fragrance = byId.get(row.fragrance_id)
    if (!fragrance) continue
    items.push({
      id: `share:${row.id}`,
      kind: row.direction,
      fragrance,
      date: row.shared_on,
      yearsAgo: currentYear - parseDay(row.shared_on).year,
      who: (row.person_name || '').trim(),
      note: (row.note || '').trim(),
    })
  }

  for (const row of anchors) {
    if (!wanted.has(row?.anchor_date)) continue
    const fragrance = byId.get(row.fragrance_id)
    if (!fragrance) continue
    items.push({
      id: `anchor:${row.id}`,
      kind: 'anchor',
      fragrance,
      date: row.anchor_date,
      yearsAgo: currentYear - parseDay(row.anchor_date).year,
      who: (row.anchor_name || '').trim(),
      note: (row.notes || '').trim(),
    })
  }

  return items.sort((a, b) => b.yearsAgo - a.yearsAgo || a.id.localeCompare(b.id))
}

export function anniversaryLead(yearsAgo) {
  if (yearsAgo === 1) return 'A year ago today'
  return `${yearsAgo} years ago today`
}

/** One plain sentence for the card, e.g. "Maya gave you Bleu de Chanel." */
export function anniversarySentence(item) {
  const name = item.fragrance?.name || 'this bottle'
  const who = item.who
  switch (item.kind) {
    case 'given': return who ? `You gave ${name} to ${who}.` : `You gave ${name} away.`
    case 'received': return who ? `${who} gave you ${name}.` : `You received ${name}.`
    case 'connection': return who ? `${name} connected you to ${who}.` : `${name} became part of a memory.`
    default: return who ? `${who}, with ${name}.` : `A memory with ${name}.`
  }
}

// Per-day dismissal is a per-viewer convenience; storage can be missing or throw.
const DISMISS_KEY = 'fos.memoryAnniversary.dismissed'

export function readDismissedDay(storage = globalThis.localStorage) {
  try { return storage?.getItem(DISMISS_KEY) || '' } catch { return '' }
}

export function writeDismissedDay(day, storage = globalThis.localStorage) {
  try { storage?.setItem(DISMISS_KEY, day) } catch { /* private mode: dismiss for this render only */ }
}
